import { CSSProperties, ReactNode } from "react";
import { GenericComponentProps, makeStyles } from "../../../utils/genericComponentProps";
import { DeprecatedTooltip } from "../DeprecatedTooltip/DeprecatedTooltip";
import { Icon, IconProps } from "../Icon/Icon";
import styles from "./Text.module.css";

export type TextSize = "sm" | "md" | "lg" | "xl" | "xxl";

export type TextColor = "primary" | "secondary" | "accent" | "danger";

export type TextProps = GenericComponentProps & {
  /** @deprecated provide text as a child node instead */
  text?: ReactNode;
  children?: ReactNode;

  size?: TextSize;
  color?: TextColor;
  customColor?: string;
  isBold?: boolean;
  fontSize?: string | number;
  lineHeight?: string | number;
  fontWeight?: number;
  letterSpacing?: string;
  isItalic?: boolean;
  textAlign?: "left" | "center" | "right";
  display?: string;
  tooltip?: string;

  className?: string;
  id?: string;

  leadingIcon?: IconProps | string;
  trailingIcon?: IconProps | string;

  marginTop?: string | number;
  marginRight?: string | number;
  marginBottom?: string | number;
  marginLeft?: string | number;

  noWrap?: boolean;
  truncate?: boolean;
  float?: "left" | "right";

  onClick?: () => void;

  title?: string;
  dataCy?: string;
};

const iconSizes = { sm: 15, md: 20, lg: 24, xl: 32, xxl: 40 };

/**
 * @deprecated Standardized in `ui-components` for consistency.
 * Use `TypographyText` instead.
 * See: https://storybook-propely-dev-site.web.app/?path=/docs/ui-components-typographytext--docs
 */
export const Text = ({
  size = "md",
  className: additionalClassName = "",
  display = "flex",
  text,
  children,
  color,
  customColor,
  textAlign,
  fontSize,
  fontWeight,
  lineHeight,
  letterSpacing,
  isItalic,
  isBold,
  noWrap,
  truncate,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  float,
  onClick,
  trailingIcon,
  leadingIcon,
  tooltip,
  title,
  id,
  dataCy,
  ...props
}: TextProps) => {
  const className = `${styles.text} ${styles[size]} ${truncate ? styles.truncate : ""} ${additionalClassName}`.trim();

  const style: CSSProperties = {
    ...makeStyles(props),
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    whiteSpace: noWrap ? "nowrap" : undefined,
    display,
    float,
    color:
      color === "secondary"
        ? "var(--text-2-color)"
        : color === "accent"
          ? "var(--primary-color)"
          : color === "danger"
            ? "var(--main-red-700)"
            : customColor,
    fontWeight: isBold ? 600 : fontWeight,
    letterSpacing: letterSpacing ?? "normal",
    fontStyle: isItalic ? "italic" : undefined,
    textAlign: textAlign || undefined,
    fontSize,
    lineHeight,
    cursor: onClick ? "pointer" : undefined,
  };

  // If textAlign is present, set display to "block"
  if (textAlign) {
    style.display = "block";
  }

  return (
    <p className={className} id={id} style={style} onClick={onClick} data-cy={dataCy} title={title}>
      {leadingIcon && (
        <span style={{ display: "flex" }}>
          {typeof leadingIcon === "string" ? <Icon name={leadingIcon} marginRight={4} size={iconSizes[size]} /> : <Icon {...leadingIcon} />}
        </span>
      )}
      {text ?? children}
      {trailingIcon && (
        <span style={{ display: "flex" }}>
          {typeof trailingIcon === "string" ? (
            <Icon name={trailingIcon} marginLeft={4} size={iconSizes[size]} cursor={onClick ? "pointer" : "default"} />
          ) : (
            <Icon {...trailingIcon} cursor={onClick ? "pointer" : "default"} />
          )}
        </span>
      )}
      {tooltip && <DeprecatedTooltip marginLeft={2} title={tooltip} />}
    </p>
  );
};
